// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable @typescript-eslint/no-misused-promises */

import { produce } from "immer";
import _ from "lodash";
import moment from "moment";
import PropTypes from "prop-types";
import React, { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Accordion, Button, Checkbox, Dropdown, Icon, Popup, Segment, Table } from "semantic-ui-react";
import { useAuth0 } from "../../../../auth/auth0";
import { useModuleNavigation } from "../../../../hooks/useModuleNavigation";
import { useUserAuth } from "../../../../hooks/useUserAuth";
import * as trialActions from "../../../../redux/actions/trialActions";
import AnalysisDropDownLists from "../../../Lumber/AnalysisDropDownLists";
import * as analysisDropDownListFunctions from "../../../Lumber/AnalysisDropDownLists/AnalysisDropDownListFunctions";
import DataAnalysisCriteria from "../../../Lumber/DataAnalysisCriteria";
import { useTrialAnalysis, useTrialAnalysisDispatch } from "../../Trials/TrialAnalysis/TrialAnalysisContext";
import {
	buildFlightAssessments,
	buildGroundDataAssessments,
	buildSummaryTableSelectionsFromPreset,
} from "../../Trials/TrialAnalysis/utils";
import "./dropdown.css";
import "./style.css";

const SummaryTableSelections = ({ fetchingData, rawTrialOptions, rawPresetOptions }) => {
	const dispatch = useDispatch();
	const { getTokenSilently } = useAuth0();
	const userAuth = useUserAuth();
	const moduleNavigation = useModuleNavigation();

	const DATE_FORMAT = "M/D/YYYY";

	const { pending, isDirty, display } = useTrialAnalysis();
	const { changeSettings, commitSettings, changeDisplaySettings } = useTrialAnalysisDispatch();

	const experimentalDesignOptions = useMemo(() => {
		if (!rawTrialOptions || rawTrialOptions.experimentalDesignFactors.length === 0) return undefined;
		const opts = rawTrialOptions.experimentalDesignFactors.map((ed, idx) => ({
			key: ed.id,
			text: `EXP${idx} - ${ed.name}`,
			value: ed.id,
		}));
		return opts;
	}, [rawTrialOptions]);

	const meanComparisonOptions = useMemo(() => {
		if (!rawTrialOptions) return undefined;
		const opts = _.map(rawTrialOptions.meanComparisonOptions, (mco) => ({
			key: mco.id,
			value: mco.id,
			text: mco.name,
		}));
		return opts;
	}, [rawTrialOptions]);

	const alphaOptions = useMemo(() => {
		if (!rawTrialOptions) return undefined;
		const opts = _.chain(rawTrialOptions.alphaOptions)
			.map((ao) => ({
				key: ao.id,
				value: ao.id,
				text: ao.value,
			}))
			.orderBy([(o) => Number(o.text)], "asc")
			.value();
		return opts;
	}, [rawTrialOptions]);

	const presetOptions = useMemo(() => {
		if (!rawTrialOptions) return undefined;

		let opts = [{ key: "none", value: "none", text: "None" }];

		opts = opts.concat(
			_.map(rawTrialOptions.summaryTablePresets, (p) => {
				const opt = { key: p.id, value: p.id, text: p.name };
				return opt;
			}),
		);

		return opts;
	}, [rawTrialOptions]);

	const qrTypeOptions = useMemo(() => {
		if (!rawTrialOptions) return undefined;
		const opts = _.map(rawTrialOptions.quantifiedRegionTypeOptions, (qrt) => ({
			key: qrt.id,
			value: qrt.id,
			text: qrt.name,
		}));
		return opts;
	}, [rawTrialOptions]);

	//-- UI Control
	const [accordionActiveIndex, setAccordionActiveIndex] = useState(false);
	const [savingAssessmentSelection, setSavingAssessmentSelection] = useState(false);
	const [checkAll, setCheckAll] = useState(false);

	//-- Data Sources
	const allAssessmentTypes = useMemo(() => {
		if (rawPresetOptions) {
			let groundDataOpts = _.map(rawTrialOptions.groundDataOptions, ({ groundDataAssessments }) => {
				return groundDataAssessments;
			});
			groundDataOpts = _.flatten(groundDataOpts);

			//-- Ground Data Presets
			let groundDataPresets = analysisDropDownListFunctions.getGroundDataPresetsForTrialAnalysis(groundDataOpts);
			//-- Growth Curve Presets
			let growthCurvePresets = analysisDropDownListFunctions.getGrowthCurvePresets(rawTrialOptions.growthCurveOptions);
			let analyses = groundDataPresets.concat(growthCurvePresets);

			//-- Time series Presets for Stand Counts
			let analysesDataOpts = _.map(rawTrialOptions.trialAnalysisOptions, ({ analysisTypes }) => {
				return analysisTypes;
			});
			analysesDataOpts = _.flatten(analysesDataOpts);
			analysesDataOpts = _.filter(analysesDataOpts, (ado) => {
				return ado.name.includes("Stand Count");
			});
			let analysesPresets = analysisDropDownListFunctions.getTimeSeriesPresetsForTrialAnalysis(analysesDataOpts);
			analyses = analyses.concat(analysesPresets);
			return analyses;
		}
	}, [rawPresetOptions, rawTrialOptions]);

	//-- Open or close the accordion on click
	const accordionClick = () => {
		const newIndex = !accordionActiveIndex;
		setAccordionActiveIndex(newIndex);
	};

	//-- check all selected assessments to be checked or unchecked
	function toggleCheckAll() {
		let selections = _.cloneDeep(pending.summaryTableSelections);
		selections.map((selection) => {
			selection.assessments?.map((assessment) => {
				if (assessment.enabled) {
					assessment.checked = !checkAll;
				}
			});
		});
		changeSettings({ summaryTableSelections: selections });
	}

	//-- Add the selected growth curve option to the summary table selections and get the data
	function growthCurveOptionSelected(value, index) {
		const growthCurve = _.find(rawTrialOptions.growthCurveOptions, { curveModelName: value });
		if (!growthCurve || pending.summaryTableSelections[index]?.id === value) return;

		if (!fetchingData) {
			changeSettings({ summaryTablePresetId: "none" });
		}

		// displayName comes from the same lookup buildInitialSummaryTableSelections uses
		const matchingDisplayName = _.find(
			rawTrialOptions.presetOptions.curveModelAnalysisDisplayNames,
			(dn) =>
				dn.analysisId === growthCurve.analysisId &&
				dn.analysisTypeId === growthCurve.analysisTypeId &&
				dn.primaryCurveModelAnalysisId === growthCurve.curveModelAnalysisId &&
				(dn.primaryQuantifiedRegionTypeId === growthCurve.quantifiedRegionTypeId ||
					dn.secondaryQuantifiedRegionTypeId === growthCurve.quantifiedRegionTypeId),
		);

		const newSelections = _.cloneDeep(pending.summaryTableSelections);
		const selection = {
			id: null,
			name: null,
			assessments: [],
			isGrowthCurveAnalysis: false,
		};

		selection.assessments = null;
		selection.isCurveModel = true;
		selection.isAnalysis = false;
		selection.isGrowthCurveAnalysis = true;
		selection.id = value;
		selection.name = value;
		selection.displayName = matchingDisplayName?.name ?? value.split(" (")[0];
		selection.analysisId = growthCurve.analysisId;
		selection.curveModelAnalysisId = growthCurve.curveModelAnalysisId;
		selection.analysisTypeId = growthCurve.analysisTypeId;
		selection.startDate = growthCurve.curveModelAnalysisStartDate;
		selection.endDate = growthCurve.curveModelAnalysisEndDate;
		selection.initialLoad = false;

		newSelections[index] = selection;
		changeSettings({ summaryTableSelections: newSelections });
	}

	//-- Add the selected growth flight option to the summary table selections and get the data
	function flightOptionSelected(value, index, quantifiedRegionTypeId) {
		const qrId = quantifiedRegionTypeId ?? qrTypeOptions[0].value;

		// Look up directly from rawTrialOptions — no assessmentOptions prop needed
		const analysisType = rawTrialOptions.trialAnalysisOptions
			.flatMap((tao) => tao.analysisTypes)
			.find((at) => at.id === value);
		const name = analysisType?.name ?? value;

		const newSelections = _.cloneDeep(pending.summaryTableSelections);
		const selection = {
			id: null,
			name: null,
			assessments: [],
			isGrowthCurveAnalysis: false,
		};

		selection.curveModelAnalysisId = null;
		selection.isCurveModel = false;
		selection.isAnalysis = true;
		selection.id = value;
		selection.analysisValue = value;
		selection.analysisTypeId = analysisType?.analysisTypeId; // stored so SummaryTable queries don't need assessmentTypes
		selection.quantifiedRegionTypeOptionId = qrId;
		selection.name = name;
		selection.displayName = name;
		selection.assessments = buildFlightAssessments(rawTrialOptions, value, qrId, name);
		selection.initialLoad = false;

		newSelections[index] = selection;
		changeSettings({ summaryTableSelections: newSelections });
		// getSingleAssessmentTrialData(value, null, null, qrId, null);
	}

	//-- Add the selected ground data option to the summary table selections and get the data
	function groundDataOptionSelected(value, index) {
		if (pending.summaryTableSelections[index]?.analysisValue === value) return;

		if (!fetchingData) {
			changeSettings({ summaryTablePresetId: "none" });
		}

		// Look up directly from rawTrialOptions — no assessmentOptions prop needed
		const gda = rawTrialOptions.groundDataOptions
			.flatMap((gdo) => gdo.groundDataAssessments)
			.find((a) => a.id === value);
		const name = gda?.name ?? value;

		const newSelections = _.cloneDeep(pending.summaryTableSelections);
		const selection = {
			id: null,
			name: null,
			assessments: [],
			isGrowthCurveAnalysis: false,
		};

		selection.curveModelAnalysisId = null;
		selection.isCurveModel = false;
		selection.isAnalysis = false;
		selection.id = value;
		selection.analysisValue = value;
		selection.name = name;
		selection.displayName = name;
		selection.assessments = buildGroundDataAssessments(rawTrialOptions, value, name);
		selection.initialLoad = false;

		newSelections[index] = selection;
		changeSettings({ summaryTableSelections: newSelections });
	}

	//-- Adds an empty record to the summary table selections array on click
	function addData() {
		const newSummaryTableSelections = pending.summaryTableSelections.concat({
			id: null,
			name: null,
			assessments: [],
			isGrowthCurveAnalysis: false,
		});
		changeSettings({ summaryTablePresetId: "none", summaryTableSelections: newSummaryTableSelections }); //verify
	}

	//-- Remove the selected option from the summary table selections array on click
	function removeData(index) {
		changeSettings({
			summaryTableSelections: _.filter(pending.summaryTableSelections, (_, i) => {
				return i != index;
			}),
		});
	}

	//-- Save the selected assessment options on click
	async function saveAssessmentSelection() {
		const accessToken = await getTokenSilently();
		setSavingAssessmentSelection(true);
		dispatch(
			trialActions.saveAssessmentSelectionForSummaryTable(
				pending.summaryTableSelections,
				accessToken,
				userAuth.currentClientId,
				moduleNavigation.trialId,
			),
		)
			.then(() => {
				setSavingAssessmentSelection(false);
				toast.success("Assessment selections have been saved");
			})
			.catch((err) => {
				setSavingAssessmentSelection(false);
				console.log(err);
				toast.error("Failed to save assessment selections.");
			});
	}

	//-- Updates the summary table selection assessments to be checked or unchecked on click
	function updateAssessmentChecked(index, subIndex) {
		const updatedSelection = produce(pending.summaryTableSelections, (draft) => {
			const selectedSummary = draft[index];
			const assessments = selectedSummary.assessments;
			assessments[subIndex] = {
				...assessments[subIndex],
				["checked"]: !assessments[subIndex].checked,
			};

			draft[index].assessments = assessments;
		});

		changeSettings({ summaryTableSelections: updatedSelection });
	}

	function updateSelectedAnalysis(value, index, displayName) {
		if (_.some(rawTrialOptions.growthCurveOptions, { curveModelName: value })) {
			growthCurveOptionSelected(value, index, displayName);
		} else if (
			//-- check if selected value is ground data
			_.some(rawTrialOptions.groundDataOptions, (gdo) => {
				return _.some(gdo.groundDataAssessments, { id: value });
			})
		) {
			groundDataOptionSelected(value, index);
		} else if (
			_.some(rawTrialOptions.trialAnalysisOptions, (tao) => {
				return _.some(tao.analysisTypes, { id: value });
			})
		) {
			flightOptionSelected(value, index, displayName);
		}
	}

	const handleExperimentalDesignChange = useCallback(
		(expId) => {
			if (!rawTrialOptions) return;
			const exp = _.find(rawTrialOptions.experimentalDesignFactors, (exp) => exp.id === expId);
			if (exp) {
				changeDisplaySettings({
					experimentalDesignId: expId,
					treatmentNames: exp.treatmentNames,
					isRCBD: exp.factorType === "RCBD",
				});
			}
		},
		[rawTrialOptions, changeDisplaySettings],
	);

	return (
		<Segment className="ribbonBanner" style={{ margin: "0 -1em" }}>
			<Accordion fluid active={1}>
				<Accordion.Title active={accordionActiveIndex} style={{ cursor: "unset" }}>
					<div style={{ display: "flex", gap: "15px", alignItems: "center" }}>
						<div
							onClick={accordionClick}
							style={{ display: "flex", fontSize: "1.28571429rem", fontWeight: "700", cursor: "pointer" }}
						>
							<Icon name="dropdown" />
							Summary Table Selections
						</div>
						{experimentalDesignOptions?.length && (
							<div style={{ display: "grid" }}>
								<label htmlFor="dropdown-select-exp-design" style={{ display: "unset" }}>
									Experimental Design
								</label>
								<Dropdown
									id="dropdown-select-exp-design"
									selection
									search
									fluid
									disabled={fetchingData || savingAssessmentSelection}
									options={experimentalDesignOptions ?? []}
									value={display.experimentalDesignId}
									onChange={(_, { value }) => {
										handleExperimentalDesignChange(value);
									}}
								/>
							</div>
						)}
						<div style={{ display: "grid" }}>
							<label htmlFor="dropdown-select-mean-comparison" style={{ display: "unset" }}>
								Mean Comparison
							</label>
							<Dropdown
								id="dropdown-select-mean-comparison"
								selection
								search
								fluid
								disabled={fetchingData || savingAssessmentSelection}
								options={meanComparisonOptions}
								value={pending.meanComparisonOptionId}
								onChange={(_, { value }) => {
									changeSettings({ meanComparisonOptionId: value });
								}}
							/>
						</div>
						<div style={{ display: "grid" }}>
							<label htmlFor="dropdown-select-alpha" style={{ display: "unset" }}>
								Alpha
							</label>
							<Dropdown
								id="dropdown-select-alpha"
								selection
								search
								fluid
								disabled={fetchingData || savingAssessmentSelection}
								options={alphaOptions}
								value={pending.alphaOptionId}
								onChange={(_, { value }) => {
									changeSettings({ alphaOptionId: value });
								}}
							/>
						</div>
						<div style={{ display: "grid" }}>
							<label htmlFor="dropdown-select-preset" style={{ display: "unset" }}>
								Preset
							</label>
							<Dropdown
								id="dropdown-select-preset"
								selection
								search
								disabled={fetchingData || savingAssessmentSelection}
								options={presetOptions}
								value={pending.summaryTablePresetId}
								onChange={(_event, { value }) => {
									const preset = _.find(rawTrialOptions.summaryTablePresets, (stp) => stp.id === value);
									const selections = buildSummaryTableSelectionsFromPreset(rawTrialOptions, preset);
									changeSettings({ summaryTablePresetId: value, summaryTableSelections: selections });
								}}
							/>
						</div>
						<div style={{ display: "grid" }}>
							<label htmlFor="form-assessment">Remove Exclusions</label>
							<div style={{ marginTop: 10 }}>
								<DataAnalysisCriteria
									removeDamagedOrExcluded={pending.removeDamagedOrExcluded}
									removeOutliers={pending.removeOutliers}
									outlierType={pending.outlierType}
									setRemoveDamagedOrExcluded={(value) => {
										changeSettings({ removeDamagedOrExcluded: value });
									}}
									setRemoveOutliers={(value) => {
										changeSettings({ removeOutliers: value });
									}}
									setOutlierType={(value) => {
										changeSettings({ outlierType: value });
									}}
									setNeedToUpdateAnalytics={() => {}}
								/>
								<Popup
									trigger={<Icon style={{ marginLeft: 10, verticalAlign: "top" }} name="info circle" />}
									content="When toggled to the 'on' position any regions the user has previously marked as excluded will be discarded from analyses"
								/>
							</div>
						</div>
						{/*<div style={{flexGrow:"1"}}></div>*/}
						<div style={{ display: "grid", alignSelf: "center" }}>
							<Button //style={{marginTop:"20px"}}
								primary={!isDirty}
								negative={isDirty}
								content="Update Analytics"
								onClick={() => {
									// updateAnalytics();
									commitSettings();
									setAccordionActiveIndex(false);
								}}
								disabled={fetchingData}
								loading={fetchingData}
							/>
						</div>
					</div>
				</Accordion.Title>
				<Accordion.Content active={accordionActiveIndex}>
					<div style={{ overflowY: "scroll", overflowX: "hidden", height: "50vh" }}>
						<Table selectable>
							<Table.Header>
								<Table.Row>
									{/* <Table.HeaderCell width={4} style={{ display: "flex" }}>
									<p style={{ marginLeft: "60px", width: "50px" }}>Data Type</p>
									<p style={{ marginLeft: "60px" }}>Assessment</p>
								</Table.HeaderCell> */}
									<Table.HeaderCell width={5} />
									<Table.HeaderCell />
								</Table.Row>
							</Table.Header>
							<Table.Body>
								{_.map(pending.summaryTableSelections, (selection, index) => {
									return (
										<Table.Row key={index}>
											<Table.Cell>
												<div style={{ display: "flex", flexDirection: "row" }}>
													<Button
														icon="minus"
														disabled={fetchingData || savingAssessmentSelection}
														onClick={() => {
															removeData(index);
															changeSettings({ summaryTablePresetId: "none" });
														}}
													/>
													{_.some(rawTrialOptions.trialAnalysisOptions, (tao) => {
														return _.some(tao.analysisTypes, { id: selection.id });
													}) && (
														<Dropdown
															selection
															fluid
															search
															disabled={fetchingData || savingAssessmentSelection}
															options={qrTypeOptions}
															value={selection.quantifiedRegionTypeOptionId}
															onChange={(event, { value }) => {
																const newState = [...pending.summaryTableSelections];
																newState[index] = {
																	...newState[index],
																	quantifiedRegionTypeOptionId: value,
																};
																changeSettings({ summaryTableSelections: newState });
																flightOptionSelected(newState[index].analysisValue, index, value);
															}}
														/>
													)}
													{rawPresetOptions && allAssessmentTypes && userAuth.isReady ? (
														<AnalysisDropDownLists
															rawPresetOptions={rawPresetOptions}
															quantifiedRegionTypes={[]}
															displayAssessmentTypes={_.filter(allAssessmentTypes, function (at) {
																let analysisExists = pending.summaryTableSelections.find((x) => x.id === at.value);
																return at.value === selection.id || !analysisExists;
															})}
															allAssessmentTypes={allAssessmentTypes}
															updateSelectedAnalysis={updateSelectedAnalysis}
															index={index}
															defaultAssessment={selection.id}
															initialLoad={selection.initialLoad}
															useSummaryTableStyles={true}
															isApAdmin={userAuth.isApAdmin}
															setSummaryTablePreset={(value) => {
																changeSettings({ summaryTablePresetId: value });
															}}
															summaryTableSelections={pending.summaryTableSelections}
															shouldUpdateAnalysisTypeId={false}
														/>
													) : (
														<></>
													)}
												</div>
											</Table.Cell>
											<Table.Cell>
												<div
													style={{
														display: "flex",
														width: "100%",
														height: "100%",
														justifyContent: "center",
														gap: "20px",
													}}
												>
													{_.map(selection.assessments, (a, subIndex) => {
														return (
															a.enabled && (
																<div key={`${index}_${subIndex}`}>
																	<p style={{ display: "inline-block", margin: "0" }}>
																		{moment(a.date).local().format(DATE_FORMAT)}
																	</p>
																	<Checkbox
																		checked={a.checked}
																		disabled={fetchingData || savingAssessmentSelection}
																		onClick={() => {
																			updateAssessmentChecked(index, subIndex);
																			changeSettings({ summaryTablePresetId: "none" });
																		}}
																		style={{ display: "inline-block", marginLeft: "5px", marginBottom: "-2px" }}
																	/>
																</div>
															)
														);
													})}
												</div>
											</Table.Cell>
										</Table.Row>
									);
								})}
							</Table.Body>
							<Table.Footer>
								<Table.Row>
									<Table.HeaderCell colSpan={3}>
										{pending.summaryTableSelections.length >=
										_.filter(allAssessmentTypes, (ao) => {
											return !ao.key.includes("Header");
										}).length ? null : (
											<Button
												disabled={fetchingData || savingAssessmentSelection}
												onClick={() => {
													addData();
												}}
											>
												Add Data
											</Button>
										)}
										<Button
											loading={savingAssessmentSelection}
											disabled={fetchingData || savingAssessmentSelection}
											onClick={saveAssessmentSelection}
										>
											Save Assessment Selection
										</Button>
										<Checkbox
											style={{ marginLeft: "5px" }}
											label="Check All"
											disabled={fetchingData || savingAssessmentSelection}
											defaultChecked={checkAll}
											onClick={() => {
												//-- If user manually clicks checkbox, turn off presets
												changeSettings({ summaryTablePresetId: "none" });
												setCheckAll(!checkAll);
												toggleCheckAll();
											}}
										/>
									</Table.HeaderCell>
								</Table.Row>
							</Table.Footer>
						</Table>
					</div>
				</Accordion.Content>
			</Accordion>
		</Segment>
	);
};

SummaryTableSelections.propTypes = {
	fetchingData: PropTypes.bool.isRequired,
	rawTrialOptions: PropTypes.object.isRequired,
	rawPresetOptions: PropTypes.object,
};

export default SummaryTableSelections;
